<template>
  <div class="factory-management">
    <div class="page-header">
      <h2>工厂管理</h2>
      <div class="header-actions">
        <a-button type="primary" @click="handleAdd">
          <template #icon>
            <PlusOutlined />
          </template>
          新增工厂
        </a-button>
        <a-button @click="loadFactories" :loading="loading">
          <template #icon>
            <ReloadOutlined />
          </template>
          刷新
        </a-button>
      </div>
    </div>

    <a-card class="filter-card">
      <a-form layout="inline">
        <a-form-item label="工厂名称">
          <a-input
            v-model:value="searchForm.name"
            placeholder="请输入工厂名称"
            allow-clear
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="状态">
          <a-select
            v-model:value="searchForm.status"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option :value="undefined">全部</a-select-option>
            <a-select-option :value="1">正常</a-select-option>
            <a-select-option :value="0">停用</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch">
              <template #icon>
                <SearchOutlined />
              </template>
              查询
            </a-button>
            <a-button @click="handleReset">
              重置
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="factories"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="record.status == 1 ? 'green' : 'red'">
              {{ record.status == 1 ? '正常' : '停用' }}
            </a-tag>
          </template>
          <template v-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="handleEdit(record)">
                编辑
              </a-button>
              <a-popconfirm
                title="确定要删除这个工厂吗？"
                @confirm="handleDelete(record)"
              >
                <a-button type="link" size="small" danger>
                  删除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="modalVisible"
      :title="modalTitle"
      @ok="handleSubmit"
      @cancel="handleCancel"
      :confirm-loading="submitting"
      width="600px"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-item
          label="工厂名称"
          name="name"
          :rules="[
            { required: true, message: '请输入工厂名称' },
            { max: 100, message: '长度不超过100个字符' }
          ]"
        >
          <a-input v-model:value="formData.name" placeholder="请输入工厂名称" />
        </a-form-item>
        <a-form-item
          label="状态"
          name="status"
          :rules="[{ required: true, message: '请选择状态' }]"
        >
          <a-radio-group v-model:value="formData.status">
            <a-radio :value="1">正常</a-radio>
            <a-radio :value="0">停用</a-radio>
          </a-radio-group>
        </a-form-item>
        <a-form-item
          label="备注"
          name="remark"
          :rules="[{ max: 500, message: '长度不超过500个字符' }]"
        >
          <a-textarea
            v-model:value="formData.remark"
            placeholder="请输入备注"
            :rows="4"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons-vue'
import { factoryApi } from '@/api'

export default {
  name: 'Factory',
  components: {
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined
  },
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const factories = ref([])
    const modalVisible = ref(false)
    const formRef = ref(null)
    const editingId = ref(null)

    const searchForm = reactive({
      name: '',
      status: undefined
    })

    const formData = reactive({
      name: '',
      status: 1,
      remark: ''
    })

    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 条`
    })

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80
      },
      {
        title: '工厂名称',
        dataIndex: 'name',
        key: 'name',
        width: 200
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 100
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 180
      },
      {
        title: '更新时间',
        dataIndex: 'updateTime',
        key: 'updateTime',
        width: 180
      },
      {
        title: '操作',
        key: 'action',
        width: 150,
        fixed: 'right'
      }
    ]

    const modalTitle = computed(() => {
      return editingId.value ? '编辑工厂' : '新增工厂'
    })

    const loadFactories = async () => {
      loading.value = true
      try {
        const result = await factoryApi.list({
          name: searchForm.name || undefined,
          status: searchForm.status || undefined,
          pageNo: pagination.current,
          pageSize: pagination.pageSize
        })

        if (result.code === '0') {
          factories.value = result.data.records || []
          pagination.total = result.data.total || 0
        } else {
          message.error(result.msg || '加载工厂列表失败')
        }
      } catch (error) {
        console.error('加载工厂列表失败:', error)
        message.error('加载工厂列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadFactories()
    }

    const handleSearch = () => {
      pagination.current = 1
      loadFactories()
    }

    const handleReset = () => {
      searchForm.name = ''
      searchForm.status = undefined
      pagination.current = 1
      loadFactories()
    }

    const handleAdd = () => {
      editingId.value = null
      formData.name = ''
      formData.status = 1
      formData.remark = ''
      modalVisible.value = true
    }

    const handleEdit = (record) => {
      editingId.value = record.id
      formData.name = record.name
      formData.status = record.status !== undefined ? record.status : 1
      formData.remark = record.remark || ''
      modalVisible.value = true
    }

    const handleDelete = async (record) => {
      try {
        const result = await factoryApi.delete(record.id)

        if (result.code === '0') {
          message.success('删除成功')
          loadFactories()
        } else {
          message.error(result.msg || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        message.error('删除失败')
      }
    }

    const handleSubmit = async () => {
      try {
        await formRef.value.validateFields()

        submitting.value = true

        let result
        if (editingId.value) {
          result = await factoryApi.update({
            id: editingId.value,
            name: formData.name,
            status: formData.status,
            remark: formData.remark
          })
        } else {
          result = await factoryApi.create({
            name: formData.name,
            status: formData.status,
            remark: formData.remark
          })
        }

        if (result.code === '0') {
          message.success(editingId.value ? '更新成功' : '创建成功')
          modalVisible.value = false
          loadFactories()
        } else {
          message.error(result.msg || '操作失败')
        }
      } catch (error) {
        if (error.errorFields) {
          return
        }
        console.error('操作失败:', error)
        message.error('操作失败')
      } finally {
        submitting.value = false
      }
    }

    const handleCancel = () => {
      modalVisible.value = false
      formData.name = ''
      formData.status = 1
      formData.remark = ''
      editingId.value = null
    }

    onMounted(() => {
      loadFactories()
    })

    return {
      loading,
      submitting,
      factories,
      modalVisible,
      modalTitle,
      formRef,
      formData,
      searchForm,
      columns,
      pagination,
      loadFactories,
      handleTableChange,
      handleSearch,
      handleReset,
      handleAdd,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleCancel
    }
  }
}
</script>

<style scoped>
.factory-management {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.filter-card {
  margin-bottom: 16px;
}
</style>
